import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, Settings, RotateCcw } from 'lucide-react';

const DukeSlider = ({ 
  score = { t: 47, date: new Date() }, 
  history = [], 
  readOnly = false, 
  className = '' 
}) => {
  const canvasRef = useRef(null);
  const [focusShape, setFocusShape] = useState(null);
  const [dragShape, setDragShape] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [options, setOptions] = useState({
    PromisStar: true,
    Goals: true,
    XAxisNums: true,
    Labels: true,
    Colors: false,
  });
  const [tableData, setTableData] = useState([]);
  
  // Store arrow positions in state to persist during drag
  const [arrowPositions, setArrowPositions] = useState({
    improvement: null,
    worsening: null
  });

  const colors = ['#FBB4AE', '#B3CDE3', '#CCEBC5', '#DECBE4', '#FED9A6', '#FFFFCC', '#E5D8BD', '#FDDAEC', '#CCCCCC', '#F2F2F2'];

  // Activity items for the correlation table
  const activityItems = [
    { name: 'Do chores', scores: [29.16538, 34.86767, 38.66041, 43.56038, 55.27645] },
    { name: 'Get in & out of car', scores: [22.7643, 27.71922, 32.68525, 38.61007, 52.60686] },
    { name: 'Go up & down stairs', scores: [30.80473, 36.09393, 39.5658, 44.18533, 55.34689] },
    { name: 'Run errands & shop', scores: [25.20097, 30.8615, 35.33345, 40.36223, 53.51351] },
    { name: 'Bend down & pick up clothing', scores: [26.96554, 31.79253, 36.02505, 40.83608, 53.26517] },
    { name: 'Lift 10 lbs/5kg above shoulder', scores: [31.8244, 35.91352, 38.59226, 42.39383, 53.68631] },
    { name: 'Health limits vigorous activities', scores: [36.85422, 43.8697, 48.79602, 53.06453, 60.21543] },
    { name: 'Health limits bathing or dressing', scores: [27.72223, 31.58612, 35.44511, 39.1029, 52.05832] },
    { name: 'Health limits carrying a trash bag', scores: [25.78041, 30.82164, 34.60281, 38.42434, 52.38593] },
    { name: 'Health limits moderate activities', scores: [28.94289, 35.27626, 40.12916, 44.18555, 54.88729] },
  ];

  // Load options from localStorage on mount
  useEffect(() => {
    const newOptions = { ...options };
    Object.keys(options).forEach(key => {
      const stored = localStorage.getItem(`duke3_${key}`);
      if (stored !== null) {
        newOptions[key] = stored === 'true';
      }
    });
    setOptions(newOptions);
  }, []);

  // Save options to localStorage
  const saveOptions = useCallback((newOptions) => {
    Object.keys(newOptions).forEach(key => {
      localStorage.setItem(`duke3_${key}`, newOptions[key]);
    });
    setOptions(newOptions);
  }, []);

  // Scale X coordinate based on canvas width
  const scaleX = useCallback((x) => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    
    const width = canvas.width;
    const offset = 1.5;
    const scale = 100 / (100 - 50);
    const range = width / 10;
    return (x * range * scale) - (offset * range * scale);
  }, []);

  // Find closest value in array
  const closest = useCallback((arr, num) => {
    let curr = arr[0];
    let diff = Math.abs(num - curr);
    for (let val = 0; val < arr.length; val++) {
      const newDiff = Math.abs(num - arr[val]);
      if (newDiff < diff) {
        diff = newDiff;
        curr = arr[val];
      }
    }
    return curr;
  }, []);

  // Calculate activity table based on current focus or drag
  const calculateTable = useCallback(() => {
    // Determine which score to show based on focus
    let showScore = score.t;
    
    if (dragShape && dragShape.goal) {
      // If dragging, use the current drag position
      showScore = dragShape.goal.target * 10;
    } else if (focusShape && focusShape.goal) {
      // If focused on a shape, use its position
      showScore = focusShape.goal.target * 10;
    }
    
    // Map activities to their difficulty levels based on the score
    const newTableData = activityItems.map(item => {
      const best = closest(item.scores, showScore);
      const index = item.scores.indexOf(best);
      return {
        name: item.name,
        selectedIndex: index,
        scores: item.scores
      };
    });
    
    setTableData(newTableData);
  }, [focusShape, dragShape, score.t, closest]);

  // Create a shape object with bounding box and unique id
  const createShape = useCallback((type, coords, fill, stroke, label, x, goal = null) => {
    let x1 = Infinity, y1 = Infinity, x2 = 0, y2 = 0;
    
    // Calculate bounding box
    coords.forEach(point => {
      x1 = Math.min(x1, point.x);
      y1 = Math.min(y1, point.y);
      x2 = Math.max(x2, point.x);
      y2 = Math.max(y2, point.y);
    });
    
    // Add a unique id to help with identification during rendering
    const id = goal ? `${type}-${goal.type}-${Date.now()}` : `${type}-${x}-${Date.now()}`;
    
    return {
      type,
      id,
      coords,
      x,
      x1,
      y1,
      x2,
      y2,
      fill,
      stroke,
      label,
      goal
    };
  }, []); 

  // Draw a rectangle on canvas
  const drawBox = useCallback((context, x, y, width, height, stroke, fill) => {
    context.beginPath();
    context.rect(x, y, width, height);
    context.fillStyle = fill;
    context.strokeStyle = stroke;
    context.fill();
    context.stroke();
  }, []);

  // Draw a shape on canvas
  const drawShape = useCallback((context, shape) => {
    if (!shape || !shape.coords || shape.coords.length === 0) return;
    
    // Enable antialiasing for smoother rendering
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    
    context.beginPath();
    context.moveTo(shape.coords[0].x, shape.coords[0].y);
    
    for (let i = 1; i < shape.coords.length; i++) {
      context.lineTo(shape.coords[i].x, shape.coords[i].y);
    }
    
    context.closePath();
    context.fillStyle = shape.fill;
    context.fill();
    
    if (shape.stroke) {
      context.strokeStyle = shape.stroke;
      context.lineWidth = 1;
      context.stroke();
    }
    
    // Don't draw numerical labels for arrows (we'll show score in a separate UI element)
    
    // Draw label for other shapes (not arrows)
    if (shape.label && options.Labels && shape.type !== 'arrow') {
      const labelY = 165;
      context.font = "14px Arial";
      context.textAlign = "center";
      
      // Draw white outline for label
      context.fillStyle = "#F0F0F0";
      for (let dx = -2; dx < 3; dx++) {
        for (let dy = -2; dy < 3; dy++) {
          context.fillText(shape.label, shape.x + dx, labelY + dy);
        }
      }
      
      context.fillStyle = "#000000";
      context.fillText(shape.label, shape.x, labelY);
    }
  }, [focusShape, options.Labels]);

  // Main drawing function
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    
    // Clear the canvas first to prevent ghosting
    context.clearRect(0, 0, canvas.width, canvas.height);
    
    // Reset canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = 170;

    const width = canvas.width;
    const height = canvas.height - 20;

    // Draw background
    drawBox(context, 0, 0, width, height, '#f0f0f0', '#f0f0f0');

    // Draw white box (slider background)
    const whiteBoxHeight = 80;
    drawBox(context, 0, height / 2 - whiteBoxHeight / 2, width, whiteBoxHeight, '#ffffff', '#ffffff');

    // Draw black ruler line
    const blackBoxHeight = 20;
    drawBox(context, 0, height / 2 - blackBoxHeight / 2, width, blackBoxHeight, '#000000', '#000000');

    // Draw axis numbers and ticks
    if (options.XAxisNums) {
      context.font = "18px Arial";
      context.fillStyle = "black";
      context.textAlign = "center";

      // Minor ticks
      for (let i = 0; i <= 9; i += 0.1) {
        const x = scaleX(i);
        drawBox(context, x, 68, 1, 4, '#ffffff', '#ffffff');
      }

      // Medium ticks
      for (let i = 0; i <= 9; i += 0.5) {
        const x = scaleX(i);
        drawBox(context, x, 68, 1, 8, '#ffffff', '#ffffff');
      }

      // Major ticks and numbers
      for (let i = 1; i <= 8; i++) {
        const x = scaleX(i);
        context.fillText(i * 10, x, 105);
        drawBox(context, x, 68, 2, 12, '#ffffff', '#ffffff');
      }
    }

    // Draw all shapes (no need to filter since we're updating shapes in place)
    shapes.forEach(shape => {
      drawShape(context, shape);
    });
    
    // Draw focus shape with clean, black outline
    if (focusShape && (!dragShape || focusShape !== dragShape)) {
      // Enable antialiasing for smoother lines
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      
      // Draw a clean, solid black outline
      context.lineWidth = 2;
      context.strokeStyle = '#000000';
      context.beginPath();
      context.moveTo(focusShape.coords[0].x, focusShape.coords[0].y);
      
      for (let i = 1; i < focusShape.coords.length; i++) {
        context.lineTo(focusShape.coords[i].x, focusShape.coords[i].y);
      }
      
      context.closePath();
      context.stroke();
      context.lineWidth = 1;
    }
  }, [shapes, focusShape, dragShape, options, scaleX, drawBox, drawShape]);

  // Generate shapes based on current data
  const generateShapes = useCallback(() => {
    // Start with a fresh array to avoid duplicate shapes
    const newShapes = [];

    // Add current PROMIS score triangle
    if (options.PromisStar) {
      const cx = scaleX(score.t / 10);
      const tWidth = 15;
      const triangleCoords = [
        { x: cx, y: 118 },
        { x: cx + tWidth, y: 145 },
        { x: cx - tWidth, y: 145 },
        { x: cx, y: 118 }
      ];
      
      const label = score.date ? score.date.toLocaleDateString() : 'Current';
      const shape = createShape('triangle', triangleCoords, '#ffcc00', '#000000', label, cx, { target: score.t / 10 });
      newShapes.push(shape);
    }

    // Add two blue arrows with persisted positions
    if (options.Goals) {
      // Arrow below (Meaningful Improvement) - points UP
      const improvementTarget = arrowPositions.improvement || Math.min(score.t / 10 + 1, 8);
      const improvementX = scaleX(improvementTarget);
      const improvementY = 100;
      
      // Create upward pointing arrow
      const improvementArrowCoords = [
        { x: improvementX, y: improvementY - 15 }, // Top point
        { x: improvementX - 8, y: improvementY },  // Top left
        { x: improvementX - 3, y: improvementY },  // Left corner
        { x: improvementX - 3, y: improvementY + 15 }, // Left bottom
        { x: improvementX + 3, y: improvementY + 15 }, // Right bottom
        { x: improvementX + 3, y: improvementY },  // Right corner
        { x: improvementX + 8, y: improvementY }   // Top right
      ];
      
      const improvementGoal = { target: improvementTarget, type: 'improvement', label: 'Meaningful Improvement' };
      const improvementShape = createShape('arrow', improvementArrowCoords, '#2563eb', '#1e40af', '', improvementX, improvementGoal);
      newShapes.push(improvementShape);
      
      // Arrow above (Meaningful Worsening) - points DOWN
      const worseningTarget = arrowPositions.worsening || Math.max(score.t / 10 - 1, 1);
      const worseningX = scaleX(worseningTarget);
      const worseningY = 50;
      
      // Create downward pointing arrow
      const worseningArrowCoords = [
        { x: worseningX, y: worseningY + 15 }, // Bottom point
        { x: worseningX - 8, y: worseningY },  // Bottom left
        { x: worseningX - 3, y: worseningY },  // Left corner
        { x: worseningX - 3, y: worseningY - 15 }, // Left top
        { x: worseningX + 3, y: worseningY - 15 }, // Right top
        { x: worseningX + 3, y: worseningY },  // Right corner
        { x: worseningX + 8, y: worseningY }   // Bottom right
      ];
      
      const worseningGoal = { target: worseningTarget, type: 'worsening', label: 'Meaningful Worsening' };
      const worseningShape = createShape('arrow', worseningArrowCoords, '#2563eb', '#1e40af', '', worseningX, worseningGoal);
      newShapes.push(worseningShape);
    }

    setShapes(newShapes);
  }, [options, score, scaleX, createShape, focusShape, arrowPositions]);

  // Point-in-polygon test
  const inside = useCallback((point, coords) => {
    // Ray casting algorithm to determine if point is inside polygon
    let inside = false;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    // Handle null or empty coords
    if (!coords || coords.length === 0) return false;

    for (let i = 0; i < coords.length; i++) {
      const coord = coords[i];
      minX = Math.min(minX, coord.x);
      minY = Math.min(minY, coord.y);
      maxX = Math.max(maxX, coord.x);
      maxY = Math.max(maxY, coord.y);
    }

    // Quick check if point is outside bounding box
    if (point.x < minX || point.x > maxX || point.y < minY || point.y > maxY) {
      return false;
    }

    // Add padding for easier clicking of arrow shapes
    if (minY < 65 || maxY > 85) { // This is an arrow (above or below the slider)
      const expandedMinX = minX - 15;
      const expandedMaxX = maxX + 15;
      const expandedMinY = minY - 15;
      const expandedMaxY = maxY + 15;
      
      if (point.x >= expandedMinX && point.x <= expandedMaxX && 
          point.y >= expandedMinY && point.y <= expandedMaxY) {
        return true;
      }
    }

    // Ray casting algorithm
    for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
      const xi = coords[i].x;
      const yi = coords[i].y;
      const xj = coords[j].x;
      const yj = coords[j].y;

      const intersect = ((yi > point.y) !== (yj > point.y)) && 
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);

      if (intersect) inside = !inside;
    }

    return inside;
  }, []);

  // Get canvas relative coordinates
  const getCanvasCoordinates = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }, []);

  // Handle mouse/touch events
  const handlePointerDown = useCallback((event) => {
    if (readOnly) return;
    
    const point = getCanvasCoordinates(event);
    
    // Check which shape is being clicked
    for (let i = shapes.length - 1; i >= 0; i--) {
      const shape = shapes[i];
      const hitPadding = shape.type === 'arrow' ? 25 : 15;
      
      if (inside(point, shape.coords) || 
          (point.x >= shape.x1 - hitPadding && point.x <= shape.x2 + hitPadding && 
           point.y >= shape.y1 - hitPadding && point.y <= shape.y2 + hitPadding)) {
           
        setFocusShape(shape);
        
        // Only arrow shapes can be dragged
        if (shape.type === 'arrow') {
          // Store reference to the actual shape, not a copy
          setDragShape(shape);
          document.body.style.cursor = 'grabbing';
        }
        
        return;
      }
    }
    
    // If no shape is clicked, clear focus
    setFocusShape(null);
  }, [readOnly, shapes, inside, getCanvasCoordinates]);

  const handlePointerMove = useCallback((event) => {
    if (!dragShape || readOnly) return;
    
    const point = getCanvasCoordinates(event);
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    event.preventDefault();
    
    // Calculate new target position based on mouse X
    const width = canvas.width;
    const offset = 1.5;
    const scale = 100 / (100 - 50);
    const range = width / 10;
    
    // Reverse the scaleX calculation to get the target value
    let newTarget = ((point.x + (offset * range * scale)) / (range * scale));
    newTarget = Math.max(1, Math.min(8, newTarget));
    
    // Update arrow position in persistent state
    setArrowPositions(prev => ({
      ...prev,
      [dragShape.goal.type]: newTarget
    }));
    
    // Update the dragged shape's position directly
    if (dragShape.type === 'arrow') {
      // Update target value
      dragShape.goal.target = newTarget;
      
      // Calculate new X position
      const newX = scaleX(newTarget);
      const dx = newX - dragShape.x;
      
      // Update the shape's x coordinate and all coordinate points
      dragShape.x = newX;
      dragShape.coords.forEach(coord => {
        coord.x += dx;
      });
      
      // Recalculate bounding box
      let x1 = Infinity, y1 = Infinity, x2 = 0, y2 = 0;
      dragShape.coords.forEach(coord => {
        x1 = Math.min(x1, coord.x);
        y1 = Math.min(y1, coord.y);
        x2 = Math.max(x2, coord.x);
        y2 = Math.max(y2, coord.y);
      });
      dragShape.x1 = x1;
      dragShape.y1 = y1;
      dragShape.x2 = x2;
      dragShape.y2 = y2;
    }
    
    // Recalculate table data immediately
    calculateTable();
    
    // Redraw canvas with updated positions
    drawCanvas();
  }, [dragShape, readOnly, getCanvasCoordinates, calculateTable, drawCanvas, scaleX]);

  const handlePointerUp = useCallback(() => {
    if (dragShape) {
      // The shape has already been updated during drag, so just persist the position
      const newTarget = dragShape.goal.target;
      setArrowPositions(prev => ({
        ...prev,
        [dragShape.goal.type]: newTarget
      }));
      
      // Clear drag state
      setDragShape(null);
      document.body.style.cursor = '';
      
      // Force a redraw to ensure everything is clean
      drawCanvas();
    } else {
      setDragShape(null);
      document.body.style.cursor = '';
    }
  }, [dragShape, drawCanvas]);

  // Reset arrows to default positions
  const resetArrows = useCallback(() => {
    setFocusShape(null);
    setDragShape(null);
    setArrowPositions({
      improvement: null,
      worsening: null
    });
  }, []);

  // Effects
  useEffect(() => {
    // Initialize arrows and shapes on mount
    generateShapes();
  }, [generateShapes]);

  useEffect(() => {
    // Draw canvas when shapes or positions change
    drawCanvas();
  }, [drawCanvas]);

  useEffect(() => {
    // Update table data when needed
    calculateTable();
  }, [calculateTable]);

  // Set up event listeners for dragging
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Handle touch events with proper coordinate conversion
    const handleTouchStart = (e) => {
      e.preventDefault();
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        const fakeEvent = { clientX: touch.clientX, clientY: touch.clientY };
        handlePointerDown(fakeEvent);
      }
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length > 0 && dragShape) {
        const touch = e.touches[0];
        const fakeEvent = { clientX: touch.clientX, clientY: touch.clientY };
        handlePointerMove(fakeEvent);
      }
    };

    const handleTouchEnd = (e) => {
      handlePointerUp();
    };

    // Add all event listeners
    canvas.addEventListener('mousedown', handlePointerDown);
    canvas.addEventListener('mousemove', handlePointerMove);
    canvas.addEventListener('mouseup', handlePointerUp);
    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);

    // Cleanup function to remove event listeners
    return () => {
      canvas.removeEventListener('mousedown', handlePointerDown);
      canvas.removeEventListener('mousemove', handlePointerMove);
      canvas.removeEventListener('mouseup', handlePointerUp);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp]);

  return (
    <div className={`duke-slider-container space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
          Physical Function Assessment
        </h3>
        <p className="text-base text-gray-600 mt-2">Track your physical functioning with interactive goals</p>
        <div className="flex justify-center mt-6">
          <button
            onClick={resetArrows}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset Goals
          </button>
        </div>
      </div>

      {/* Meaningful Improvement/Worsening Messages */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                </svg>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-orange-900">Meaningful Worsening</h4>
              <p className="text-sm text-orange-700">Drag the blue arrow above the slider to set your threshold</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-green-900">Meaningful Improvement</h4>
              <p className="text-sm text-green-700">Drag the blue arrow below the slider to set your improvement goal</p>
            </div>
          </div>
        </div>
      </div>

      {/* Canvas Slider */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/20 border border-gray-100 dark:border-gray-700 p-6">
        <div className="text-center text-sm font-medium text-gray-700 mb-4 flex items-center justify-center gap-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
          </svg>
          <span>Worst physical functioning</span>
          <div className="w-16 h-px bg-gradient-to-r from-gray-300 to-gray-500"></div>
          <span>Better physical functioning</span>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>
        
        <div className="relative">
          <canvas
            ref={canvasRef}
            height="170"
            className="w-full rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20"
            style={{ touchAction: 'none' }}
          />
          
          {/* Single Score Display */}
          <div className="absolute top-2 right-2 px-4 py-2 bg-white/95 dark:bg-gray-800/95 rounded-md shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">
                {focusShape ? 
                  focusShape.type === 'arrow' ? 
                    (focusShape.goal.type === 'improvement' ? 'Improvement Goal:' : 'Worsening Threshold:') 
                    : 'Current Score:' 
                  : 'Current Score:'}
              </span>
              <span className="text-base font-bold text-gray-900">
                {focusShape ? 
                  focusShape.type === 'arrow' ? 
                    (focusShape.goal.target * 10).toFixed(1)
                    : (score.t).toFixed(1)
                  : (score.t).toFixed(1)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Correlation Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/20 border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Activity Difficulty Correlation</h4>
              <p className="text-sm text-gray-600">See how your current score relates to daily activities</p>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {/* Physical Activities Section */}
          <div className="p-6">
            <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Physical Activities</h5>
            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-700">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Unable to do</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Much difficulty</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Some difficulty</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Little difficulty</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">No difficulty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {tableData.slice(0, 6).map((item, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      {[0, 1, 2, 3, 4].map(colIndex => (
                        <td 
                          key={colIndex}
                          className={`px-4 py-4 text-sm transition-all duration-200 ${
                            item.selectedIndex === colIndex 
                              ? 'font-bold text-gray-900 bg-gray-50 dark:bg-gray-900 border-l-4 border-gray-400'
                              : 'text-gray-400 font-normal'
                          }`}
                        >
                          {item.selectedIndex === colIndex ? (
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-gray-600"></div>
                              <span className="font-bold">{item.name}</span>
                            </div>
                          ) : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Health Limitations Section */}
          <div className="px-6 pb-6">
            <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Health Limitations</h5>
            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-700">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Cannot do</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Quite a lot</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Somewhat</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Very little</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Not at all</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {tableData.slice(6, 10).map((item, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      {[0, 1, 2, 3, 4].map(colIndex => (
                        <td 
                          key={colIndex}
                          className={`px-4 py-4 text-sm transition-all duration-200 ${
                            item.selectedIndex === colIndex 
                              ? 'font-bold text-gray-900 bg-gray-50 dark:bg-gray-900 border-l-4 border-gray-400'
                              : 'text-gray-400 font-normal'
                          }`}
                        >
                          {item.selectedIndex === colIndex ? (
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-gray-600"></div>
                              <span className="font-bold">{item.name}</span>
                            </div>
                          ) : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DukeSlider;